/**
 * Copyright IBM Corp. 2021
 *
 * This source code is licensed under the IBM Public License Version 1.0 found in the
 * LICENSE file in the root directory of this source tree.
 */

import TriModelApi from "./api/TriModelApi";

export class TriModel {
  constructor(modelAndViewName, instanceId, onError) {
    this.modelAndViewName = modelAndViewName;
    this.instanceId = instanceId;
    this.onError = onError;
  }

  async getRecord(
    context,
    query,
    multiContextIds,
    countOnly,
    reserveIncludeUnavailable
  ) {
    const { modelAndViewName, instanceId } = this;
    try {
      return await TriModelApi.fetchModel({
        modelAndViewName,
        instanceId,
        context,
        type: TriModelApi.ModelRequestTypes.GET,
        query,
        multiContextIds,
        countOnly,
        reserveIncludeUnavailable,
      });
    } catch (error) {
      this.handleError(error);
    }
  }

  async createRecord(
    context,
    data,
    query,
    refresh,
    actionGroup,
    action,
    wfParametersMap
  ) {
    const { modelAndViewName, instanceId } = this;
    try {
      return await TriModelApi.fetchModel({
        modelAndViewName,
        instanceId,
        context,
        type: TriModelApi.ModelRequestTypes.CREATE,
        query,
        data,
        actionGroup,
        action,
        wfParametersMap,
        refresh,
      });
    } catch (error) {
      this.handleError(error);
    }
  }

  async deleteRecord(
    context,
    recordIds,
    query,
    refresh,
    actionGroup,
    action,
    wfParametersMap
  ) {
    const { modelAndViewName, instanceId } = this;
    const data = this.wrapRecordIdsAsData(recordIds);
    try {
      return await TriModelApi.fetchModel({
        modelAndViewName,
        instanceId,
        context,
        type: TriModelApi.ModelRequestTypes.DELETE,
        query,
        data,
        actionGroup,
        action,
        wfParametersMap,
        refresh,
      });
    } catch (error) {
      this.handleError(error);
    }
  }

  async updateRecord(
    context,
    data,
    query,
    refresh,
    actionGroup,
    action,
    wfParametersMap
  ) {
    const { modelAndViewName, instanceId } = this;
    try {
      return await TriModelApi.fetchModel({
        modelAndViewName,
        instanceId,
        context,
        type: TriModelApi.ModelRequestTypes.SAVE,
        query,
        data,
        actionGroup,
        action,
        wfParametersMap,
        refresh,
      });
    } catch (error) {
      this.handleError(error);
    }
  }

  async performAction(
    context,
    recordIds,
    query,
    refresh,
    actionGroup,
    action,
    wfParametersMap
  ) {
    const { modelAndViewName, instanceId } = this;
    const data = this.wrapRecordIdsAsData(recordIds);
    try {
      return await TriModelApi.fetchModel({
        modelAndViewName,
        instanceId,
        context,
        type: TriModelApi.ModelRequestTypes.SAVE,
        query,
        data,
        actionGroup,
        action,
        wfParametersMap,
        refresh,
      });
    } catch (error) {
      this.handleError(error);
    }
  }

  async addRecord(
    context,
    recordIds,
    query,
    refresh,
    actionGroup,
    action,
    wfParametersMap
  ) {
    const { modelAndViewName, instanceId } = this;
    const data = this.wrapRecordIdsAsData(recordIds);
    try {
      return await TriModelApi.fetchModel({
        modelAndViewName,
        instanceId,
        context,
        type: TriModelApi.ModelRequestTypes.ADD,
        query,
        data,
        actionGroup,
        action,
        wfParametersMap,
        refresh,
      });
    } catch (error) {
      this.handleError(error);
    }
  }

  async removeRecord(
    context,
    recordIds,
    query,
    refresh,
    actionGroup,
    action,
    wfParametersMap
  ) {
    const { modelAndViewName, instanceId } = this;
    const data = this.wrapRecordIdsAsData(recordIds);
    try {
      return await TriModelApi.fetchModel({
        modelAndViewName,
        instanceId,
        context,
        type: TriModelApi.ModelRequestTypes.REMOVE,
        query,
        data,
        actionGroup,
        action,
        wfParametersMap,
        refresh,
      });
    } catch (error) {
      this.handleError(error);
    }
  }

  wrapRecordIdsAsData(recordIds) {
    if (Array.isArray(recordIds)) {
      return recordIds.map((recordId) => {
        return {
          _id: recordId,
        };
      });
    } else if (recordIds != null) {
      return {
        _id: recordIds,
      };
    }
    return null;
  }

  async getQueryMetadata(context) {
    const { modelAndViewName, instanceId } = this;
    try {
      return await TriModelApi.fetchModel({
        modelAndViewName,
        instanceId,
        context,
        type: TriModelApi.ModelRequestTypes.GET_QUERY_METADATA,
      });
    } catch (error) {
      this.handleError(error);
    }
  }

  handleError(error) {
    if (this.onError != null) this.onError(error);
    throw error;
  }
}
